/// Radio "mic-key" click played before each ATC voice readback.
///
/// Generated programmatically with the Web Audio API — a short
/// 1.2 kHz sine burst with a fast attack/decay envelope, capped
/// at ~70 ms total. Cheap (no sample loaded), distinct enough to
/// give the readback radio texture without competing with it.
///
/// Like speech, autoplay is gated by browser policy: an
/// `AudioContext` constructed before any user gesture starts in
/// the `suspended` state. We `resume()` lazily on the first call
/// — if it's still suspended after that (player hasn't clicked
/// anywhere yet), we drop the blip silently.

const SAMPLE_FREQ_HZ = 1200
const ATTACK_SEC = 0.01
const SUSTAIN_SEC = 0.04
const RELEASE_SEC = 0.02

let ctx: AudioContext | null = null

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctx) return ctx
  type ACtor = typeof AudioContext
  const Ctor: ACtor | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: ACtor }).webkitAudioContext
  if (!Ctor) return null
  try {
    ctx = new Ctor()
    return ctx
  } catch {
    return null
  }
}

const BLIP_VOLUME_PREF_KEY = 'flatout.web.blipVolume'
const DEFAULT_BLIP_VOLUME = 0.18

function getBlipVolume(): number {
  try {
    const raw = window.localStorage.getItem(BLIP_VOLUME_PREF_KEY)
    if (raw === null) return DEFAULT_BLIP_VOLUME
    const n = Number(raw)
    if (!Number.isFinite(n)) return DEFAULT_BLIP_VOLUME
    return Math.max(0, Math.min(1, n))
  } catch {
    return DEFAULT_BLIP_VOLUME
  }
}

export function setBlipVolume(v: number): void {
  try {
    window.localStorage.setItem(
      BLIP_VOLUME_PREF_KEY,
      String(Math.max(0, Math.min(1, v))),
    )
  } catch {
    // best-effort
  }
}

/// Play one mic-key click. Resolves immediately; the actual sound
/// runs ~70 ms on the audio thread.
export function playBlip(): void {
  const c = getCtx()
  if (!c) return
  if (c.state === 'suspended') {
    c.resume().catch(() => {
      /* swallow — first click hasn't happened yet */
    })
    if (c.state === 'suspended') return
  }
  const vol = getBlipVolume()
  if (vol <= 0) return

  const now = c.currentTime
  const osc = c.createOscillator()
  osc.type = 'sine'
  osc.frequency.value = SAMPLE_FREQ_HZ

  const gain = c.createGain()
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(vol, now + ATTACK_SEC)
  gain.gain.setValueAtTime(vol, now + ATTACK_SEC + SUSTAIN_SEC)
  gain.gain.linearRampToValueAtTime(
    0,
    now + ATTACK_SEC + SUSTAIN_SEC + RELEASE_SEC,
  )

  osc.connect(gain).connect(c.destination)
  osc.start(now)
  osc.stop(now + ATTACK_SEC + SUSTAIN_SEC + RELEASE_SEC + 0.01)
}
